import React, { useState, useEffect, useMemo } from "react";
import { useLocation, Outlet } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { loadStripe } from "@stripe/stripe-js/pure";
import { Elements } from "@stripe/react-stripe-js";
import { useQuery } from "urql";

import { SUBSCRIPTIONS_PLAN_QUERY } from "../../graphql/queries";
import { getString, getPlanData } from "../../utilities";
import { userRoles, billingPlatform, stripePublishableKeys } from "../../config";
import PlanButton from "./planButton";
import SubscribedModal from "./subscribedModal";
import Features from "./features";
import OptionallyDisplayed from "../global/optionallyDisplayed";
import LoadingAnim from "../global/loadingAnim";
import { gtmSubscribed } from "../../utilities/gtm";
import ErrorMessage from "../global/errorMessage";

export default function Subscribe() {
  const stripePromise = useMemo(() => loadStripe(stripePublishableKeys[process.env.NODE_ENV]), []);

  const location = useLocation();

  const [submitting, setSubmitting] = useState(false);
  const [plan, setPlan] = useState("family");
  const [promo, setPromo] = useState(null);
  const [token, setToken] = useState("");
  const [familyData, setFamilyData] = useState(null);
  const [classroomData, setClassroomData] = useState(null);

  const methods = useForm({ mode: "onChange" });
  const { getValues, reset } = methods;
  const { accessName, accessCode } = getValues();

  useEffect(() => {
    reset();

    const routePlan = location.pathname.split("/").slice(-1)[0];
    if (["free", "family", "classroom"].includes(routePlan)) {
      setPlan(routePlan);
    }

    if (promo && !promo.planGroups.includes(routePlan.toUpperCase())) {
      setPromo(null);
    }
  }, [location]);

  const [result] = useQuery({ query: SUBSCRIPTIONS_PLAN_QUERY, variables: { billingPlatform } });
  const { fetching: loading, error, data } = result;

  useEffect(() => {
    if (data) {
      setFamilyData(getPlanData(data, userRoles.family, promo));
      setClassroomData(getPlanData(data, userRoles.classroom, promo));
    }
  }, [plan, data, promo]);

  const renderErrors = (error) => {
    if (error) {
      return (
        <div className="wrapper padded">
          <div className="container-medium">
            <ErrorMessage error={error} />
          </div>
        </div>
      );
    }

    return null;
  };

  const subscribeSuccess = (data, plan, options = {}) => {
    const {
      signup: { subscription, token },
    } = data;
    gtmSubscribed(plan, options);
    window.localStorage.setItem("planId", plan.planId);
    window.localStorage.setItem("billingPlatform", subscription.billingPlatform);

    setSubmitting(false);
    setToken(token);
  };

  const renderForm = () => {
    return (
      <Elements stripe={stripePromise}>
        <FormProvider {...methods}>
          <Outlet
            context={{
              success: subscribeSuccess,
              promo,
              setPromo,
              familyData,
              classroomData,
              submitting,
              setSubmitting,
              errors: methods.formState.errors,
            }}
          />
        </FormProvider>
      </Elements>
    );
  };

  return (
    <>
      <div className="subscribe">
        <div className="wrapper padded">
          <h1>{getString("join.3")}</h1>

          <h2>{getString("subscribe.cta")}</h2>
          <div className="planselect">
            <PlanButton to="free" color="pt-yellow" />

            <PlanButton to="family" color="pt-cyan" promo={promo} />

            <PlanButton to="classroom" color="pt-blue" promo={promo} />
          </div>
        </div>

        <OptionallyDisplayed doDisplay={loading}>
          <div className="wrapper padded">
            <LoadingAnim />
          </div>
        </OptionallyDisplayed>
        <OptionallyDisplayed doDisplay={!loading}>
          {renderErrors(error)}
          <OptionallyDisplayed doDisplay={!!familyData && !!classroomData}>{renderForm()}</OptionallyDisplayed>
          <Features familyData={familyData} classroomData={classroomData} promo={promo} />
        </OptionallyDisplayed>
      </div>
      <div className="wrapper header-shadow" />
      <SubscribedModal token={token} accessName={accessName} accessCode={accessCode} />
    </>
  );
}
